import React, { useState } from 'react';
import {
  TextField,
  Box,
  Grid,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Chip,
  Alert,
  Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { getEffectiveTimezone } from '@/utils/timezoneUtils';
import { LeagueFormData, DiscoveryPlatform, EditingLeague } from './LeagueFormDialog';

export interface CustomLeagueFormContentProps {
  formData: LeagueFormData;
  fieldErrors: Record<string, string>;
  onInputChange: (field: keyof LeagueFormData, value: any) => void;
  detailsReadOnly: boolean;
  readOnly: boolean;
  editingLeague: EditingLeague | null;
  fromCatalog: boolean;
  discoveryPlatform: DiscoveryPlatform;
  syncTime: Dayjs | null;
  onSyncTimeChange: (newTime: Dayjs | null) => void;
  user: any;
  orgSettings: any;
  pendingAssignment?: { playerName: string; teamName: string };
  getSyncSettingsSummary: () => string;
}

const CustomLeagueFormContent: React.FC<CustomLeagueFormContentProps> = ({
  formData,
  fieldErrors,
  onInputChange,
  detailsReadOnly,
  readOnly,
  editingLeague,
  fromCatalog,
  discoveryPlatform,
  syncTime,
  onSyncTimeChange,
  user,
  orgSettings,
  pendingAssignment,
  getSyncSettingsSummary,
}) => {
  const [syncExpanded, setSyncExpanded] = useState(false);
  const [playoffExpanded, setPlayoffExpanded] = useState(false);
  const [standingsExpanded, setStandingsExpanded] = useState(false);

  return (
    <Box sx={{ px: 3, pt: 2, pb: 2 }}>
      {pendingAssignment && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>{pendingAssignment.playerName}</strong> will be assigned to <strong>{pendingAssignment.teamName}</strong> when this league is created.
        </Alert>
      )}
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a catalog league with administrator managed settings.
        </Alert>
      )}
      {detailsReadOnly && !readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          League settings are provided by the catalog and cannot be changed.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="League Website"
            value={formData.website}
            onChange={(e) => onInputChange('website', e.target.value)}
            placeholder="https://example.com/league"
            helperText={fieldErrors.website || (!editingLeague ? "Website URL from gotsport, sincsports, or modular11" : "Website URL for the league (used for automatic schedule importing when enabled)")}
            error={!!fieldErrors.website}
            required={!editingLeague}
            disabled={detailsReadOnly}
          />
          {discoveryPlatform && !editingLeague && !fromCatalog && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {discoveryPlatform === 'gotsport'
                ? 'This is a GotSport event page with multiple schedules. Click "Next" to select which ones to import.'
                : 'This is a SincSports league page with multiple divisions. Click "Next" to select which ones to import.'
              }
            </Alert>
          )}
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="League Name"
            value={formData.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            required
            error={!!fieldErrors.name}
            helperText={fieldErrors.name}
            disabled={detailsReadOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Division"
            value={formData.division}
            onChange={(e) => onInputChange('division', e.target.value)}
            placeholder="e.g., U14 NPL 2, Boys Premier"
            disabled={detailsReadOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            multiline
            rows={2}
            disabled={detailsReadOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="League Logo URL"
            value={formData.logo}
            onChange={(e) => onInputChange('logo', e.target.value)}
            placeholder="https://example.com/logo.png"
            helperText="URL to the league logo image (optional)"
            disabled={detailsReadOnly}
          />
        </Grid>
        {/* Auto Update Section */}
        <Grid item xs={12}>
          <Box
            onClick={() => setSyncExpanded(!syncExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.50' },
              borderRadius: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
              Auto Update {!syncExpanded && getSyncSettingsSummary()}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {syncExpanded ? '▼' : '▶'}
            </Typography>
          </Box>

          <Collapse in={syncExpanded}>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoSyncEnabled}
                    onChange={(e) => onInputChange('autoSyncEnabled', e.target.checked)}
                    disabled={detailsReadOnly || !formData.website.trim()}
                  />
                }
                label="Enable automatic updates"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                {formData.website.trim()
                  ? "When enabled, this league will automatically update on a configured schedule."
                  : "A website URL is required to enable automatic updates. Please add a website URL above."
                }
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  {!editingLeague ? (
                    <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
                      Update time will be automatically assigned between 1-5 AM in your timezone.
                    </Typography>
                  ) : (
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="en"
                    >
                      <TimePicker
                        label="Update Time"
                        value={syncTime}
                        onChange={onSyncTimeChange}
                        disabled={detailsReadOnly || !formData.autoSyncEnabled || !formData.website.trim()}
                        timezone={getEffectiveTimezone(user, orgSettings)}
                        slotProps={{
                          textField: {
                            fullWidth: true
                          }
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Grid>

                {editingLeague && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth disabled={detailsReadOnly || !formData.autoSyncEnabled || !formData.website.trim()}>
                        <InputLabel>Update Frequency</InputLabel>
                        <Select
                          value={formData.syncFrequency}
                          onChange={(e) => onInputChange('syncFrequency', e.target.value)}
                          label="Update Frequency"
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="every_2_days">Every 2 Days</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth disabled={detailsReadOnly || !formData.autoSyncEnabled || !formData.website.trim()}>
                        <InputLabel>Update Priority</InputLabel>
                        <Select
                          value={formData.syncPriority}
                          onChange={(e) => onInputChange('syncPriority', e.target.value)}
                          label="Update Priority"
                        >
                          <MenuItem value={0}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label="Low" size="small" color="default" />
                              Low Priority
                            </Box>
                          </MenuItem>
                          <MenuItem value={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label="Normal" size="small" color="primary" />
                              Normal Priority
                            </Box>
                          </MenuItem>
                          <MenuItem value={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip label="High" size="small" color="warning" />
                              High Priority
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Collapse>
        </Grid>

        {/* Playoff Configuration Section */}
        <Grid item xs={12}>
          <Box
            onClick={() => setPlayoffExpanded(!playoffExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.50' },
              borderRadius: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
              Playoff Configuration {!playoffExpanded && `(${formData.playoffsEnabled ? `${formData.playoffSpots} spots` : 'Disabled'})`}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {playoffExpanded ? '▼' : '▶'}
            </Typography>
          </Box>
          <Collapse in={playoffExpanded}>
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.playoffsEnabled}
                    onChange={(e) => onInputChange('playoffsEnabled', e.target.checked)}
                    disabled={detailsReadOnly}
                  />
                }
                label="Enable playoffs"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                When enabled, teams in playoff positions will be highlighted in standings tables.
              </Typography>

              {formData.playoffsEnabled && (
                <TextField
                  fullWidth
                  type="number"
                  label="Playoff Spots"
                  value={formData.playoffSpots}
                  onChange={(e) => onInputChange('playoffSpots', parseInt(e.target.value) || 4)}
                  inputProps={{ min: 1, max: 32 }}
                  helperText="Number of teams that qualify for playoffs"
                  disabled={detailsReadOnly}
                />
              )}
            </Box>
          </Collapse>
        </Grid>

        {/* Standings Configuration Section */}
        <Grid item xs={12}>
          <Box
            onClick={() => setStandingsExpanded(!standingsExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.50' },
              borderRadius: 1
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', flexGrow: 1 }}>
              Results Configuration ({formData.standingsSource === 'imported' ? 'Imported' : 'Calculated'})
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              {standingsExpanded ? '▼' : '▶'}
            </Typography>
          </Box>

          <Collapse in={standingsExpanded}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure points awarded for match results. Used for PPG calculations when not provided by source.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Win"
                  type="number"
                  size="small"
                  value={formData.winPoints}
                  onChange={(e) => onInputChange('winPoints', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 10 }}
                  sx={{ width: '80px' }}
                  disabled={detailsReadOnly}
                />
                <TextField
                  label="Draw"
                  type="number"
                  size="small"
                  value={formData.drawPoints}
                  onChange={(e) => onInputChange('drawPoints', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 10 }}
                  sx={{ width: '80px' }}
                  disabled={detailsReadOnly}
                />
                <TextField
                  label="Loss"
                  type="number"
                  size="small"
                  value={formData.lossPoints}
                  onChange={(e) => onInputChange('lossPoints', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 10 }}
                  sx={{ width: '80px' }}
                  disabled={detailsReadOnly}
                />
              </Box>
            </Box>
          </Collapse>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomLeagueFormContent;
